import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { useKnowledgeBaseStatus } from '@/hooks/useKnowledgeBaseStatus';
import { useShootingPreferencesStatus } from '@/hooks/useShootingPreferencesStatus';
import { useNetworkConnectionsStatus } from '@/hooks/useNetworkConnectionsStatus';
import {
  getActiveStepsForUser,
  hasCompletedOnboarding,
  markOnboardingCompleted,
  type OnboardingStepConfig,
} from '@/services/data/onboarding';
import { notifyOnboardingWebhook } from '@/services/data/onboardingWebhook';
import { ONBOARDING_WEBHOOK_EVENTS } from '@/constants/onboarding';

/**
 * Mapping of step keys to their completion conditions.
 * When the condition is true, the step should be filtered out.
 */
interface StepCompletionConditions {
  knowledge_base: boolean;
  shooting_setup: boolean;
  decor_selection: boolean;
  thumbnail_selection: boolean;
  network_connection: boolean;
}

/**
 * Filters out completed steps from the provided steps array.
 * Returns the filtered steps and whether any filtering occurred.
 */
function filterCompletedSteps(
  steps: OnboardingStepConfig[],
  conditions: StepCompletionConditions
): { filteredSteps: OnboardingStepConfig[]; hasChanges: boolean } {
  const stepKeysToRemove = (Object.keys(conditions) as Array<keyof StepCompletionConditions>)
    .filter(key => conditions[key]);

  const filteredSteps = steps.filter(step => !stepKeysToRemove.includes(step.step_key as keyof StepCompletionConditions));
  const hasChanges = filteredSteps.length !== steps.length;

  return { filteredSteps, hasChanges };
}

interface UseOnboardingStepsReturn {
  // State
  steps: OnboardingStepConfig[];
  currentStep: OnboardingStepConfig | null;
  currentStepIndex: number;
  isLoading: boolean;
  isOnboardingActive: boolean;
  shouldShowOnboarding: boolean;
  
  // Navigation
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToStep: (index: number) => void;
  
  // Actions
  completeOnboarding: () => Promise<void>;
  skipOnboarding: () => Promise<void>;
  
  // Progress
  progress: { current: number; total: number; percentage: number };
  isFirstStep: boolean;
  isLastStep: boolean;
}

export function useOnboardingSteps(): UseOnboardingStepsReturn {
  const { userId } = useAuthStore();
  const { isComplete: isKnowledgeBaseComplete, isLoading: isKBLoading } = useKnowledgeBaseStatus(userId);
  const { 
    hasInstallationType, 
    hasDecorColor,
    hasThumbnailStyle,
    isLoading: isPrefsLoading 
  } = useShootingPreferencesStatus(userId);
  const {
    allNetworksConnected,
    isLoading: isNetworksLoading
  } = useNetworkConnectionsStatus(userId);
  const {
    steps,
    currentStepIndex,
    isOnboardingActive,
    isLoading,
    setLoading,
    startOnboarding,
    completeOnboarding: storeCompleteOnboarding,
    skipOnboarding: storeSkipOnboarding,
    nextStep,
    previousStep,
    setCurrentStepIndex,
    getCurrentStep,
    isFirstStep,
    isLastStep,
    getProgress,
    setCompleting,
  } = useOnboardingStore();

  const [shouldShowOnboarding, setShouldShowOnboarding] = useState(false);
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false);

  // Check onboarding status on mount
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!userId || hasCheckedStatus) return;
      
      // Wait for all status checks to complete
      if (isKBLoading || isPrefsLoading || isNetworksLoading) return;
      
      setLoading(true);
      
      try {
        // Check if user has already completed onboarding
        const statusResult = await hasCompletedOnboarding(userId);
        
        if (statusResult.success && statusResult.completed) {
          // User already completed onboarding
          setShouldShowOnboarding(false);
          setLoading(false);
          setHasCheckedStatus(true);
          return;
        }
        
        // Get active steps for user
        const stepsResult = await getActiveStepsForUser(userId);
        
        if (!stepsResult.success || !stepsResult.data || stepsResult.data.length === 0) {
          // No steps available for this user
          setShouldShowOnboarding(false);
          setLoading(false);
          setHasCheckedStatus(true);
          return;
        }
        
        // Filter steps based on existing data using completion conditions
        const initialConditions: StepCompletionConditions = {
          knowledge_base: isKnowledgeBaseComplete,
          shooting_setup: hasInstallationType,
          decor_selection: hasDecorColor,
          thumbnail_selection: hasThumbnailStyle,
          network_connection: allNetworksConnected,
        };
        const { filteredSteps } = filterCompletedSteps(stepsResult.data, initialConditions);

        if (filteredSteps.length === 0) {
          // All steps were skipped - mark onboarding as complete
          await markOnboardingCompleted(userId);
          setShouldShowOnboarding(false);
          setLoading(false);
          setHasCheckedStatus(true);
          return;
        }
        
        // Start onboarding with filtered steps
        startOnboarding(filteredSteps);
        setShouldShowOnboarding(true);
        setHasCheckedStatus(true);
        
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        setShouldShowOnboarding(false);
        setLoading(false);
        setHasCheckedStatus(true);
      }
    };

    checkOnboardingStatus();
  }, [userId, hasCheckedStatus, isKnowledgeBaseComplete, isKBLoading, hasInstallationType, hasDecorColor, hasThumbnailStyle, isPrefsLoading, allNetworksConnected, isNetworksLoading]);

  // Memoize the completion conditions to avoid recreating object on every render
  const completionConditions = useMemo<StepCompletionConditions>(() => ({
    knowledge_base: isKnowledgeBaseComplete,
    shooting_setup: hasInstallationType,
    decor_selection: hasDecorColor,
    thumbnail_selection: hasThumbnailStyle,
    network_connection: allNetworksConnected,
  }), [isKnowledgeBaseComplete, hasInstallationType, hasDecorColor, hasThumbnailStyle, allNetworksConnected]);

  // Re-filter steps when status changes during onboarding
  useEffect(() => {
    const isStatusLoading = isKBLoading || isPrefsLoading || isNetworksLoading;
    if (isStatusLoading || !isOnboardingActive || steps.length === 0) return;

    const { filteredSteps, hasChanges } = filterCompletedSteps(steps, completionConditions);

    if (!hasChanges) return;

    const currentStepKey = steps[currentStepIndex]?.step_key;
    const newIndex = currentStepKey
      ? Math.max(0, filteredSteps.findIndex(s => s.step_key === currentStepKey))
      : 0;

    startOnboarding(filteredSteps);
    if (newIndex >= 0) {
      setCurrentStepIndex(newIndex);
    }
  }, [completionConditions, isKBLoading, isPrefsLoading, isNetworksLoading, isOnboardingActive, steps, currentStepIndex, startOnboarding, setCurrentStepIndex]);

  const completeOnboarding = useCallback(async () => {
    if (!userId) return;
    
    setCompleting(true);
    
    try {
      await markOnboardingCompleted(userId);
      notifyOnboardingWebhook(userId, { event: ONBOARDING_WEBHOOK_EVENTS.ONBOARDING_COMPLETED });
      storeCompleteOnboarding();
      setShouldShowOnboarding(false);
    } catch (error) {
      console.error('Error completing onboarding:', error);
    } finally {
      setCompleting(false);
    }
  }, [userId, storeCompleteOnboarding, setCompleting]);

  const skipOnboarding = useCallback(async () => {
    if (!userId) return;
    
    setCompleting(true);
    
    try {
      await markOnboardingCompleted(userId);
      notifyOnboardingWebhook(userId, { event: ONBOARDING_WEBHOOK_EVENTS.ONBOARDING_SKIPPED });
      storeSkipOnboarding();
      setShouldShowOnboarding(false);
    } catch (error) {
      console.error('Error skipping onboarding:', error);
    } finally {
      setCompleting(false);
    }
  }, [userId, storeSkipOnboarding, setCompleting]);

  const goToNextStep = useCallback(() => {
    if (isLastStep()) {
      completeOnboarding();
    } else {
      nextStep();
    }
  }, [nextStep, isLastStep, completeOnboarding]);

  const goToPreviousStep = useCallback(() => {
    previousStep();
  }, [previousStep]);

  const goToStep = useCallback((index: number) => {
    if (index >= 0 && index < steps.length) {
      setCurrentStepIndex(index);
    }
  }, [steps.length, setCurrentStepIndex]);

  return {
    // State
    steps,
    currentStep: getCurrentStep(),
    currentStepIndex,
    isLoading: isLoading || isKBLoading || isPrefsLoading || isNetworksLoading,
    isOnboardingActive,
    shouldShowOnboarding,
    
    // Navigation
    goToNextStep,
    goToPreviousStep,
    goToStep,
    
    // Actions
    completeOnboarding,
    skipOnboarding,
    
    // Progress
    progress: getProgress(),
    isFirstStep: isFirstStep(),
    isLastStep: isLastStep(),
  };
}
